/*
 * AdminBannersPanel — gerencia o banner fullscreen que aparece pra todos
 * os funcionários no login. Só UM banner ativo por vez.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Image as ImageIcon, Upload, Loader2, Trash2, ToggleLeft, ToggleRight,
  Users, Eye, CheckCircle2, CalendarClock, StopCircle, History, Sparkles,
  Globe2, Building2, MapPin, UserSearch, X, Check,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const STATUS_META = {
  running:   { label: "No ar",     className: "bg-emerald-500 text-white" },
  scheduled: { label: "Agendado",  className: "bg-sky-500 text-white" },
  paused:    { label: "Pausado",   className: "bg-slate-400 text-white" },
  expired:   { label: "Vencido",   className: "bg-amber-500 text-white" },
  ended:     { label: "Encerrado", className: "bg-slate-600 text-white" },
};

/* -----------------------------------------------------------------------
 * AudiencePicker — selector for the banner target audience.
 *
 * The three targeting types (Postos · Contratos · Funcionários) can be
 * COMBINED on the same banner using OR semantics. Toggling all of them
 * off collapses the audience back to "Todos".
 * ----------------------------------------------------------------------- */
const AUDIENCE_TABS = [
  { key: "postos",     label: "Postos",         Icon: MapPin,      color: "sky" },
  { key: "contratos",  label: "Contratos",      Icon: Building2,   color: "indigo" },
  { key: "employees",  label: "Funcionários",   Icon: UserSearch,  color: "emerald" },
];

export function AudiencePicker({ value, onChange }) {
  const [openTab, setOpenTab] = useState(null);
  const [postosList, setPostosList] = useState(null);
  const [contratosList, setContratosList] = useState(null);
  const [empQuery, setEmpQuery] = useState("");
  const [empDebounced, setEmpDebounced] = useState("");
  const [empResults, setEmpResults] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [postoQuery, setPostoQuery] = useState("");
  const [contratoQuery, setContratoQuery] = useState("");

  const patch = (delta) => onChange({ ...value, ...delta });

  const selectedPostos = value.postos || [];
  const selectedContratos = value.contratos || [];
  const selectedEmployees = value.employees || [];
  const totalTargeted = selectedPostos.length + selectedContratos.length + selectedEmployees.length;
  const isAll = totalTargeted === 0;

  // Fetch postos when first opened
  useEffect(() => {
    if (openTab !== "postos" || postosList !== null) return;
    let alive = true;
    axios.get(`${API}/admin/postos`).then((r) => {
      if (!alive) return;
      const rows = Array.isArray(r.data)
        ? r.data
        : (r.data?.postos || []);
      setPostosList(rows.map((p) => ({
        name: p.posto || p.name || p, count: p.count || 0,
      })));
    }).catch(() => alive && setPostosList([]));
    return () => { alive = false; };
  }, [openTab, postosList]);

  useEffect(() => {
    if (openTab !== "contratos" || contratosList !== null) return;
    let alive = true;
    axios.get(`${API}/admin/contratos`).then((r) => {
      if (!alive) return;
      const rows = Array.isArray(r.data)
        ? r.data
        : (r.data?.contracts || r.data?.contratos || []);
      setContratosList(rows.map((c) => ({
        name: c.contract || c.contrato || c.name || c,
        count: c.employees_count || c.count || 0,
      })));
    }).catch(() => alive && setContratosList([]));
    return () => { alive = false; };
  }, [openTab, contratosList]);

  useEffect(() => {
    const t = setTimeout(() => setEmpDebounced(empQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [empQuery]);

  useEffect(() => {
    if (openTab !== "employees") return;
    if (empDebounced.length < 2) { setEmpResults([]); return; }
    let alive = true;
    setEmpLoading(true);
    axios.get(`${API}/admin/employees`, {
      params: { search: empDebounced, page: 1, per_page: 20 },
    }).then((r) => {
      if (!alive) return;
      setEmpResults(r.data?.employees || []);
    }).catch(() => alive && setEmpResults([]))
      .finally(() => alive && setEmpLoading(false));
    return () => { alive = false; };
  }, [empDebounced, openTab]);

  const togglePosto = (name) => {
    const nx = selectedPostos.includes(name)
      ? selectedPostos.filter((p) => p !== name)
      : [...selectedPostos, name];
    patch({ postos: nx });
  };
  const toggleContrato = (name) => {
    const nx = selectedContratos.includes(name)
      ? selectedContratos.filter((c) => c !== name)
      : [...selectedContratos, name];
    patch({ contratos: nx });
  };
  const toggleEmployee = (emp) => {
    const already = selectedEmployees.find((e) => e.id === emp.id);
    const nx = already
      ? selectedEmployees.filter((e) => e.id !== emp.id)
      : [...selectedEmployees, {
          id: emp.id, nome: emp.nome,
          matricula: emp.matricula, posto_servico: emp.posto_servico,
        }];
    patch({ employees: nx });
  };
  const clearAll = () => patch({ postos: [], contratos: [], employees: [] });

  const counts = {
    postos: selectedPostos.length,
    contratos: selectedContratos.length,
    employees: selectedEmployees.length,
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-3"
         data-testid="banner-audience-picker">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />
          Público-alvo
          <span className="ml-2 text-[10px] font-normal text-slate-500">
            (você pode combinar mais de um tipo)
          </span>
        </span>
        {isAll ? (
          <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full flex items-center gap-1">
            <Globe2 className="w-2.5 h-2.5" /> TODOS
          </span>
        ) : (
          <button type="button" onClick={clearAll}
                   data-testid="banner-audience-clear"
                   className="text-[10px] font-semibold text-rose-700 hover:text-rose-900 underline">
            Limpar seleção
          </button>
        )}
      </div>

      {/* Compound scope tabs — each one opens a sub-panel while keeping
          the other selections active. Displayed count is a live badge. */}
      <div className="grid grid-cols-3 gap-1 p-1 rounded-lg bg-white border border-slate-200">
        {AUDIENCE_TABS.map(({ key, label, Icon }) => {
          const on = openTab === key;
          const active = counts[key] > 0;
          return (
            <button key={key} type="button"
                     onClick={() => setOpenTab(on ? null : key)}
                     data-testid={`banner-audience-scope-${key}`}
                     className={`relative text-[11px] font-semibold py-1.5 rounded transition flex items-center justify-center gap-1 ${
                       on
                         ? "bg-fuchsia-600 text-white shadow-sm"
                         : active
                           ? "bg-fuchsia-50 text-fuchsia-800 border border-fuchsia-200"
                           : "text-slate-600 hover:text-fuchsia-700 hover:bg-fuchsia-50"
                     }`}>
              <Icon className="w-3 h-3" />
              {label}
              {active && (
                <span className={`ml-1 min-w-[18px] px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                  on ? "bg-white text-fuchsia-700" : "bg-fuchsia-600 text-white"
                }`}>
                  {counts[key]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {isAll && !openTab && (
        <p className="text-[11px] text-slate-500 flex items-start gap-1.5 leading-relaxed">
          <Globe2 className="w-3 h-3 mt-0.5 shrink-0" />
          Sem restrição: o banner será exibido para <b>todos os funcionários</b>.
          Escolha um ou mais tipos acima para restringir.
        </p>
      )}

      {openTab === "postos" && (
        <div className="space-y-2">
          <Input
            placeholder="Buscar posto..."
            value={postoQuery}
            onChange={(e) => setPostoQuery(e.target.value)}
            className="h-8 text-xs"
            data-testid="banner-audience-posto-search"
          />
          <div className="max-h-52 overflow-y-auto rounded border border-slate-200 bg-white divide-y">
            {postosList === null ? (
              <div className="p-4 text-center">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" />
              </div>
            ) : (postosList.length === 0 ? (
              <p className="p-3 text-center text-xs text-slate-500">
                Nenhum posto encontrado.
              </p>
            ) : (
              postosList
                .filter((p) => !postoQuery
                  || p.name.toLowerCase().includes(postoQuery.toLowerCase()))
                .map((p) => {
                  const on = selectedPostos.includes(p.name);
                  return (
                    <button key={p.name} type="button"
                             onClick={() => togglePosto(p.name)}
                             data-testid={`banner-audience-posto-item-${p.name}`}
                             className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-left text-xs transition ${
                               on ? "bg-fuchsia-50" : "hover:bg-slate-50"
                             }`}>
                      <span className="flex items-center gap-2 min-w-0">
                        <span className={`w-4 h-4 rounded border-2 shrink-0 flex items-center justify-center ${
                          on ? "bg-fuchsia-600 border-fuchsia-600" : "border-slate-300 bg-white"
                        }`}>
                          {on && <Check className="w-2.5 h-2.5 text-white" />}
                        </span>
                        <span className="truncate text-slate-800">{p.name}</span>
                      </span>
                      <span className="text-[10px] text-slate-400 shrink-0">
                        {p.count} func.
                      </span>
                    </button>
                  );
                })
            ))}
          </div>
        </div>
      )}

      {openTab === "contratos" && (
        <div className="space-y-2">
          <Input
            placeholder="Buscar contrato..."
            value={contratoQuery}
            onChange={(e) => setContratoQuery(e.target.value)}
            className="h-8 text-xs"
            data-testid="banner-audience-contrato-search"
          />
          <div className="max-h-52 overflow-y-auto rounded border border-slate-200 bg-white divide-y">
            {contratosList === null ? (
              <div className="p-4 text-center">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" />
              </div>
            ) : (contratosList.length === 0 ? (
              <p className="p-3 text-center text-xs text-slate-500">
                Nenhum contrato encontrado.
              </p>
            ) : (
              contratosList
                .filter((c) => !contratoQuery
                  || c.name.toLowerCase().includes(contratoQuery.toLowerCase()))
                .map((c) => {
                  const on = selectedContratos.includes(c.name);
                  return (
                    <button key={c.name} type="button"
                             onClick={() => toggleContrato(c.name)}
                             data-testid={`banner-audience-contrato-item-${c.name}`}
                             className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-left text-xs transition ${
                               on ? "bg-fuchsia-50" : "hover:bg-slate-50"
                             }`}>
                      <span className="flex items-center gap-2 min-w-0">
                        <span className={`w-4 h-4 rounded border-2 shrink-0 flex items-center justify-center ${
                          on ? "bg-fuchsia-600 border-fuchsia-600" : "border-slate-300 bg-white"
                        }`}>
                          {on && <Check className="w-2.5 h-2.5 text-white" />}
                        </span>
                        <span className="truncate text-slate-800">{c.name}</span>
                      </span>
                      <span className="text-[10px] text-slate-400 shrink-0">
                        {c.count} func.
                      </span>
                    </button>
                  );
                })
            ))}
          </div>
        </div>
      )}

      {openTab === "employees" && (
        <div className="space-y-2">
          <Input
            placeholder="Buscar por nome ou matrícula..."
            value={empQuery}
            onChange={(e) => setEmpQuery(e.target.value)}
            className="h-8 text-xs"
            data-testid="banner-audience-employee-search"
          />

          {selectedEmployees.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedEmployees.map((e) => (
                <span key={e.id}
                       className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-fuchsia-100 border border-fuchsia-200 text-[10px] text-fuchsia-800">
                  <span className="font-mono">{e.matricula}</span>
                  <span className="truncate max-w-[110px]">
                    {(e.nome || "").split(" ").slice(0, 2).join(" ")}
                  </span>
                  <button type="button" onClick={() => toggleEmployee(e)}
                           className="hover:bg-fuchsia-200 rounded-full p-0.5">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="max-h-44 overflow-y-auto rounded border border-slate-200 bg-white divide-y">
            {empLoading ? (
              <div className="p-4 text-center">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" />
              </div>
            ) : empDebounced.length < 2 ? (
              <p className="p-3 text-center text-[11px] text-slate-500">
                Digite ao menos 2 letras para buscar.
              </p>
            ) : empResults.length === 0 ? (
              <p className="p-3 text-center text-[11px] text-slate-500">
                Nenhum resultado.
              </p>
            ) : (
              empResults.map((e) => {
                const on = !!selectedEmployees.find((s) => s.id === e.id);
                return (
                  <button key={e.id} type="button"
                           onClick={() => toggleEmployee(e)}
                           data-testid={`banner-audience-employee-item-${e.id}`}
                           className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-xs transition ${
                             on ? "bg-fuchsia-50" : "hover:bg-slate-50"
                           }`}>
                    <span className={`w-4 h-4 rounded border-2 shrink-0 flex items-center justify-center ${
                      on ? "bg-fuchsia-600 border-fuchsia-600" : "border-slate-300 bg-white"
                    }`}>
                      {on && <Check className="w-2.5 h-2.5 text-white" />}
                    </span>
                    <span className="font-mono text-[10px] text-slate-500 shrink-0">
                      {e.matricula}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-slate-800 truncate leading-tight">
                        {e.nome}
                      </span>
                      {e.posto_servico && (
                        <span className="block text-[10px] text-slate-500 truncate leading-tight">
                          {e.posto_servico}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Serialize the audience picker's internal state to the wire format.
function audienceToPayload(a) {
  if (!a) return { scope: "all" };
  const postos = a.postos || [];
  const contratos = a.contratos || [];
  const employees = a.employees || [];
  if (!postos.length && !contratos.length && !employees.length) {
    return { scope: "all" };
  }
  return {
    scope: "targeted",
    postos,
    contratos,
    employee_ids: employees.map((e) => e.id),
  };
}

function audienceIsValid(_a) {
  // Empty audience simply means "todos" — always valid.
  return true;
}

function UploadDialog({ open, onClose, onCreated }) {
  const [mode, setMode] = useState("upload"); // "upload" | "text"
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  // Text-mode state
  const [bodyText, setBodyText] = useState("");
  const [palette, setPalette] = useState("avi_azul");
  const [ornament, setOrnament] = useState("none");
  const [orientation, setOrientation] = useState("landscape");
  const [palettes, setPalettes] = useState([]);
  const [ornaments, setOrnaments] = useState([]);
  const [textPreviewUrl, setTextPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [audience, setAudience] = useState({
    scope: "all", postos: [], contratos: [], employees: [],
  });
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMode("upload");
      setTitle(""); setDescription("");
      setStartsAt(""); setEndsAt("");
      setFile(null); setPreview(null); setBusy(false);
      setBodyText(""); setPalette("avi_azul"); setOrnament("none");
      setTextPreviewUrl(null); setPreviewing(false);
      if (fileRef.current) fileRef.current.value = "";
      // Load palettes + ornaments once dialog opens
      axios.get(`${API}/admin/banners/palettes`)
        .then((r) => {
          setPalettes(r.data?.items || []);
          setOrnaments(r.data?.ornaments || []);
        })
        .catch(() => {});
    }
  }, [open]);

  // Cleanup preview blob URL
  useEffect(() => () => {
    if (textPreviewUrl) URL.revokeObjectURL(textPreviewUrl);
  }, [textPreviewUrl]);

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const generatePreview = async () => {
    if (!title.trim() && !bodyText.trim()) {
      return toast.error("Preencha o título ou o texto.");
    }
    setPreviewing(true);
    try {
      const r = await axios.post(
        `${API}/admin/banners/generate-text/preview`,
        { title: title.trim(), body: bodyText.trim(), palette, ornament, orientation },
        { responseType: "blob", timeout: 30000 },
      );
      if (textPreviewUrl) URL.revokeObjectURL(textPreviewUrl);
      setTextPreviewUrl(URL.createObjectURL(r.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar prévia.");
    } finally { setPreviewing(false); }
  };

  const submit = async () => {
    if (startsAt && endsAt && endsAt <= startsAt) {
      return toast.error("A data final precisa ser depois da inicial.");
    }
    if (!audienceIsValid(audience)) {
      return toast.error(
        "Selecione ao menos um item no público-alvo (posto, contrato ou funcionário).",
      );
    }
    setBusy(true);
    try {
      const audiencePayload = audienceToPayload(audience);
      if (mode === "text") {
        if (!title.trim() && !bodyText.trim()) {
          setBusy(false);
          return toast.error("Preencha o título ou o texto.");
        }
        await axios.post(`${API}/admin/banners/generate-text`, {
          title: title.trim(),
          body: bodyText.trim(),
          palette,
          ornament,
          orientation,
          description: description.trim(),
          active: true,
          starts_at: startsAt || "",
          ends_at: endsAt || "",
          audience: audiencePayload,
        }, { timeout: 30000 });
      } else {
        if (!file) { setBusy(false); return toast.error("Selecione uma imagem."); }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title.trim());
        fd.append("description", description.trim());
        fd.append("active", "true");
        if (startsAt) fd.append("starts_at", startsAt);
        if (endsAt) fd.append("ends_at", endsAt);
        fd.append("audience", JSON.stringify(audiencePayload));
        await axios.post(`${API}/admin/banners/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
      }
      toast.success("Banner publicado.");
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao publicar.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"
                      data-testid="banner-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-fuchsia-800">
            <ImageIcon className="w-5 h-5" />
            Publicar Banner no Portal
          </DialogTitle>
        </DialogHeader>

        {/* Mode switcher */}
        <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-slate-100 border border-slate-200">
          <button type="button"
                   onClick={() => setMode("upload")}
                   data-testid="banner-mode-upload"
                   className={`text-xs font-semibold py-2 rounded transition ${
                     mode === "upload"
                       ? "bg-white text-fuchsia-700 shadow-sm"
                       : "text-slate-600 hover:text-slate-900"
                   }`}>
            <Upload className="w-3.5 h-3.5 inline mr-1" />
            Enviar imagem
          </button>
          <button type="button"
                   onClick={() => setMode("text")}
                   data-testid="banner-mode-text"
                   className={`text-xs font-semibold py-2 rounded transition ${
                     mode === "text"
                       ? "bg-white text-fuchsia-700 shadow-sm"
                       : "text-slate-600 hover:text-slate-900"
                   }`}>
            <Sparkles className="w-3.5 h-3.5 inline mr-1" />
            Gerar por texto
          </button>
        </div>

        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold text-slate-700">
              Título {mode === "text" ? "*" : "(opcional)"}
            </label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="Ex: Campanha de Segurança 2026" className="mt-1"
                   data-testid="banner-title" />
          </div>

          {mode === "text" && (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-700">
                  Texto principal *
                </label>
                <Textarea value={bodyText}
                           onChange={(e) => setBodyText(e.target.value)}
                           rows={3} className="mt-1 text-sm"
                           placeholder="Ex: Nova campanha AVI 2026. Confira as mudanças e mantenha-se sempre em segurança."
                           data-testid="banner-body-text" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">
                  Orientação
                </label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <button type="button"
                           onClick={() => setOrientation("landscape")}
                           data-testid="banner-orientation-landscape"
                           className={`flex items-center gap-2 justify-center px-3 py-2 rounded-lg border-2 text-xs font-semibold transition ${
                             orientation === "landscape"
                               ? "border-fuchsia-500 bg-fuchsia-50 text-fuchsia-800 ring-2 ring-fuchsia-200"
                               : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                           }`}>
                    <span className="inline-block w-8 h-5 bg-current opacity-30 rounded-sm" />
                    Paisagem <span className="text-[10px] opacity-70">16:9</span>
                  </button>
                  <button type="button"
                           onClick={() => setOrientation("portrait")}
                           data-testid="banner-orientation-portrait"
                           className={`flex items-center gap-2 justify-center px-3 py-2 rounded-lg border-2 text-xs font-semibold transition ${
                             orientation === "portrait"
                               ? "border-fuchsia-500 bg-fuchsia-50 text-fuchsia-800 ring-2 ring-fuchsia-200"
                               : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                           }`}>
                    <span className="inline-block w-4 h-6 bg-current opacity-30 rounded-sm" />
                    Retrato <span className="text-[10px] opacity-70">9:16</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700">
                  Paleta de cores
                </label>
                <div className="mt-1 flex gap-2 flex-wrap">
                  {palettes.map((p) => (
                    <button key={p.key} type="button"
                             onClick={() => setPalette(p.key)}
                             data-testid={`banner-palette-${p.key}`}
                             title={p.key}
                             className={`h-9 w-14 rounded border-2 transition ${
                               palette === p.key
                                 ? "border-fuchsia-500 ring-2 ring-fuchsia-200"
                                 : "border-slate-200 hover:border-slate-400"
                             }`}
                             style={{
                               background: `linear-gradient(180deg, ${p.top}, ${p.bottom})`,
                               boxShadow: `inset -4px 0 0 ${p.accent}`,
                             }} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">
                  Adereço / Campanha
                </label>
                <div className="mt-1 flex gap-2 flex-wrap">
                  {ornaments.map((o) => {
                    const active = ornament === o.key;
                    const isNone = o.key === "none";
                    return (
                      <button key={o.key} type="button"
                               onClick={() => setOrnament(o.key)}
                               data-testid={`banner-ornament-${o.key}`}
                               title={o.label || "Sem adereço"}
                               className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 text-[11px] font-semibold transition ${
                                 active
                                   ? "border-fuchsia-500 ring-2 ring-fuchsia-200 bg-white"
                                   : "border-slate-200 bg-slate-50 hover:border-slate-400"
                               }`}>
                        {!isNone && (
                          <span className="inline-block w-3 h-3 rounded-full"
                                 style={{ backgroundColor: o.color }} />
                        )}
                        <span className="text-slate-700">
                          {isNone ? "Nenhum" : o.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  Adiciona uma fita de conscientização + selo da campanha no canto direito.
                </p>
              </div>
              <Button type="button" variant="outline"
                       onClick={generatePreview}
                       disabled={previewing}
                       className="w-full border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50"
                       data-testid="banner-generate-preview">
                {previewing
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Gerando…</>
                  : <><Eye className="w-4 h-4 mr-2" />Gerar prévia</>}
              </Button>
              {textPreviewUrl && (
                <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50 p-2">
                  <img src={textPreviewUrl} alt="Prévia gerada"
                        data-testid="banner-text-preview"
                        className="w-full max-h-72 object-contain" />
                  <p className="text-[10px] text-slate-500 mt-1 text-center">
                    Prévia (será salva ao clicar em Publicar)
                  </p>
                </div>
              )}
            </>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-700">Descrição interna (opcional)</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                       rows={2} className="mt-1 text-sm"
                       placeholder="Contexto interno — não aparece pro funcionário"
                       data-testid="banner-description" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> Início (opcional)
              </label>
              <Input type="datetime-local" value={startsAt}
                      onChange={(e) => setStartsAt(e.target.value)}
                      className="mt-1 text-xs"
                      data-testid="banner-starts-at" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> Encerra em (opcional)
              </label>
              <Input type="datetime-local" value={endsAt}
                      onChange={(e) => setEndsAt(e.target.value)}
                      className="mt-1 text-xs"
                      data-testid="banner-ends-at" />
            </div>
          </div>
          <p className="text-[10px] text-slate-500">
            Se preencher as datas, o banner só aparece pra funcionários dentro
            desse período (fuso Brasília). Vazio = fica no ar até você desativar.
          </p>

          {mode === "upload" && (
            <div>
              <label className="text-xs font-semibold text-slate-700">
                Imagem (PNG/JPG/WEBP, máx 5 MB) *
              </label>
              <Input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
                      onChange={onFile} className="mt-1 text-xs"
                      data-testid="banner-file" />
              {preview && (
                <div className="mt-2 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 p-2">
                  <img src={preview} alt="Prévia"
                        className="w-full max-h-72 object-contain" />
                </div>
              )}
            </div>
          )}

          <AudiencePicker value={audience} onChange={setAudience} />

          <p className="text-[11px] text-slate-500">
            📌 Banners <b>direcionados</b> (posto/contrato/funcionários) coexistem com outros ativos.
            Banners <b>Todos</b> são exclusivos — publicar um novo desativa o {'"Todos"'} anterior.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={submit}
                   disabled={busy || (mode === "upload" ? !file : (!title.trim() && !bodyText.trim()))}
                   className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                   data-testid="banner-submit">
            {busy
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Publicando…</>
              : <><Upload className="w-4 h-4 mr-2" />Publicar</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AcksDialog({ open, banner, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!banner) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/banners/${banner.id}/acknowledgments`);
      setItems(r.data?.items || []);
    } catch { toast.error("Falha ao carregar."); }
    finally { setLoading(false); }
  }, [banner]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto"
                      data-testid="banner-acks-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-fuchsia-600" />
            Cientes — {banner?.title || "Banner"}
            <Badge variant="outline">{items.length}</Badge>
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-center text-sm text-slate-500 py-10">
            Ninguém deu Ciente ainda.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id}
                    className="flex items-center gap-3 p-2.5 rounded bg-slate-50 border border-slate-200"
                    data-testid={`banner-ack-item-${it.id}`}>
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {it.employee_nome} {it.employee_matricula
                      ? <span className="font-mono text-[11px] text-slate-500">
                          • {it.employee_matricula}
                        </span>
                      : null}
                  </p>
                  <p className="text-[11px] text-slate-500 font-mono">
                    {fmtDate(it.acknowledged_at)} {it.ip
                      ? <span className="ml-1">• {it.ip}</span>
                      : null}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminBannersPanel() {
  const [items, setItems] = useState([]);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [acksBanner, setAcksBanner] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [endingId, setEndingId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/banners/list`);
      setItems(r.data?.items || []);
      setTotalEmployees(r.data?.total_employees || 0);
    } catch { toast.error("Falha ao carregar banners."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (b) => {
    setTogglingId(b.id);
    try {
      await axios.patch(`${API}/admin/banners/${b.id}`,
        { active: !b.active }, { timeout: 20000 });
      toast.success(!b.active ? "Banner ativado." : "Banner pausado.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha.");
    } finally { setTogglingId(null); }
  };

  const endCampaign = async (b) => {
    if (!window.confirm(
      `Encerrar a campanha "${b.title || b.filename}"?\n\n`
      + `A imagem para de aparecer, mas o histórico com ${b.ack_count} `
      + `Ciente(s) fica registrado.`,
    )) return;
    setEndingId(b.id);
    try {
      await axios.post(`${API}/admin/banners/${b.id}/end`,
        {}, { timeout: 20000 });
      toast.success("Campanha encerrada.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao encerrar.");
    } finally { setEndingId(null); }
  };

  const remove = async (b) => {
    if (!window.confirm(
      `Excluir DEFINITIVAMENTE este banner?\n\n`
      + `Os ${b.ack_count} "cientes" registrados também serão apagados.\n`
      + `Se quiser apenas parar a exibição sem perder o histórico, use "Encerrar campanha".`,
    )) return;
    setDeletingId(b.id);
    try {
      await axios.delete(`${API}/admin/banners/${b.id}`, { timeout: 20000 });
      toast.success("Banner excluído.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir.");
    } finally { setDeletingId(null); }
  };

  const isActive = (b) =>
    ["running", "scheduled"].includes(b.computed_status);
  const active = items.filter(isActive);
  const history = items.filter((b) => !isActive(b));

  const renderItem = (b) => {
    const meta = STATUS_META[b.computed_status] || STATUS_META.paused;
    const inHistory = !isActive(b);
    return (
      <div key={b.id}
            className={`flex flex-wrap items-center gap-3 p-3 rounded-lg border ${
              b.computed_status === "running"
                ? "bg-fuchsia-50 border-fuchsia-200"
                : "bg-slate-50 border-slate-200"
            }`}
            data-testid={`banner-item-${b.id}`}>
        <div className="w-24 h-16 shrink-0 bg-white rounded border border-slate-200 overflow-hidden">
          <img src={`${API}/admin/banners/${b.id}/image`}
                alt={b.title || b.filename}
                className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 truncate">
              {b.title || b.filename}
            </span>
            <Badge className={`${meta.className} text-[10px]`}>
              {meta.label}
            </Badge>
            <Badge variant="outline" className="text-[10px] font-mono">
              {(b.size_bytes / 1024).toFixed(0)} KB
            </Badge>
            {(() => {
              const aud = b.audience || {};
              const s = aud.scope || "all";
              if (s === "all") return (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-full">
                  <Globe2 className="w-2.5 h-2.5" />TODOS
                </span>
              );
              const chips = [];
              const np = (aud.postos || []).length;
              const nc = (aud.contratos || []).length;
              const ne = (aud.employee_ids || []).length;
              if (np) chips.push(
                <span key="p" className="inline-flex items-center gap-1 text-[10px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded-full"
                       title={(aud.postos || []).join(", ")}>
                  <MapPin className="w-2.5 h-2.5" />{np}
                </span>
              );
              if (nc) chips.push(
                <span key="c" className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-800 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded-full"
                       title={(aud.contratos || []).join(", ")}>
                  <Building2 className="w-2.5 h-2.5" />{nc}
                </span>
              );
              if (ne) chips.push(
                <span key="e" className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                  <UserSearch className="w-2.5 h-2.5" />{ne}
                </span>
              );
              return <>{chips}</>;
            })()}
          </div>
          {b.description && (
            <p className="text-xs text-slate-600 mt-0.5 truncate">
              {b.description}
            </p>
          )}
          <div className="text-[10px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 font-mono">
            <span>Publicado: {fmtDate(b.created_at)}</span>
            {b.starts_at && <span>Início: {fmtDate(b.starts_at)}</span>}
            {b.ends_at && <span>Fim: {fmtDate(b.ends_at)}</span>}
            {b.ended_at && (
              <span className="text-slate-700">
                Encerrado: {fmtDate(b.ended_at)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button size="sm" variant="outline"
                  onClick={() => setAcksBanner(b)}
                  data-testid={`banner-acks-${b.id}`}
                  className="h-8 border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50">
            <Users className="w-3.5 h-3.5 sm:mr-1" />
            <span className="hidden sm:inline">
              {b.ack_count} Ciente(s) · {b.ack_percent}%
            </span>
          </Button>
          {!inHistory && (
            <>
              <Button size="sm" variant="outline"
                      onClick={() => toggle(b)}
                      disabled={togglingId === b.id}
                      data-testid={`banner-toggle-${b.id}`}
                      className="h-8"
                      title={b.active ? "Pausar (mantém tudo, só esconde)" : "Reativar"}>
                {togglingId === b.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : b.active
                    ? <ToggleRight className="w-5 h-5 text-emerald-600" />
                    : <ToggleLeft className="w-5 h-5 text-slate-400" />}
              </Button>
              <Button size="sm" variant="outline"
                      onClick={() => endCampaign(b)}
                      disabled={endingId === b.id}
                      data-testid={`banner-end-${b.id}`}
                      className="h-8 border-amber-200 text-amber-700 hover:bg-amber-50"
                      title="Encerrar campanha (vai pro histórico)">
                {endingId === b.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <StopCircle className="w-3.5 h-3.5" />}
              </Button>
            </>
          )}
          <Button size="sm" variant="outline"
                  onClick={() => remove(b)}
                  disabled={deletingId === b.id}
                  data-testid={`banner-delete-${b.id}`}
                  className="h-8 border-red-200 text-red-600 hover:bg-red-50"
                  title="Excluir definitivamente (perde o histórico)">
            {deletingId === b.id
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Trash2 className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
    );
  };

  const runningBanner = items.find((b) => b.computed_status === "running");

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center font-heading text-base">
              <ImageIcon className="w-4 h-4 mr-2 text-fuchsia-600" />
              Banner do Portal
            </CardTitle>
            <CardDescription className="text-xs">
              Imagem em tela cheia exibida no login — funcionário só acessa após dar &quot;Ciente&quot;
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setUploadOpen(true)}
                   className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                   data-testid="banner-open-upload">
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Novo Banner
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onCreated={load} />
        <AcksDialog open={!!acksBanner} banner={acksBanner}
                     onClose={() => setAcksBanner(null)} />

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8">
            <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Nenhum banner publicado ainda.</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Clique em &quot;Novo Banner&quot; pra subir o primeiro.
            </p>
          </div>
        ) : (
          <>
            {runningBanner && (
              <div className="mb-3 p-2.5 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
                <b>No ar:</b> {runningBanner.title || runningBanner.filename}
                {" — "}
                <b>{runningBanner.ack_count}</b> de {totalEmployees}{" "}
                funcionário(s) deram Ciente{" "}
                (<b>{runningBanner.ack_percent}%</b>)
              </div>
            )}

            {/* Active (running + scheduled) */}
            {active.length > 0 && (
              <div className="space-y-2 mb-4" data-testid="banner-list-active">
                {active.map(renderItem)}
              </div>
            )}
            {active.length === 0 && (
              <p className="text-xs text-slate-500 italic mb-4">
                Nenhum banner ativo no momento — publique um novo.
              </p>
            )}

            {/* History accordion */}
            {history.length > 0 && (
              <div className="border-t border-slate-200 pt-3">
                <button type="button"
                         onClick={() => setShowHistory((v) => !v)}
                         data-testid="banner-toggle-history"
                         className="flex items-center gap-2 text-xs font-semibold text-slate-700 hover:text-fuchsia-700 mb-2">
                  <History className="w-4 h-4" />
                  Histórico ({history.length})
                  <span className="text-slate-400 font-normal">
                    {showHistory ? "▲" : "▼"}
                  </span>
                </button>
                {showHistory && (
                  <div className="space-y-2 mt-2" data-testid="banner-list-history">
                    {history.map(renderItem)}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
